import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DESCEND_COST,
  LANTERNS,
  ORES,
  PICKAXES,
  ROCKS,
  WORN_SHARE,
  digCost,
  digTimeMs,
  zoneFor,
  type MineCellDto,
  type MineDiveDto,
  type MineStatusDto,
} from '@bb/shared';
import type { MineEventDto } from '../net/api';
import { AVATAR_H, AVATAR_W, drawAvatar, type AvatarLook } from '../art/avatar';
import { play } from '../audio/sound';

/**
 * The Deep Mine as a place: a 2D side view of the shaft under the Pebble Beach
 * cliffs with the player's own character in it. Walk through tunnels, and hold a
 * direction (or press and hold a rock) to dig: harder rock, and rock with
 * something inside, takes longer. The server decides every dig and checks the
 * hold time; this only draws what it's told and animates the in-between.
 */

const T = 64; // world units per square
const SURFACE_ROWS = 2; // sky and cliff above floor row 0
const STEP_MS = 150; // walking speed per square
type Dir = 'up' | 'down' | 'left' | 'right';
const DIRS: Record<Dir, [number, number]> = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] };
const KEYS: Record<string, Dir> = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right', w: 'up', s: 'down', a: 'left', d: 'right', W: 'up', S: 'down', A: 'left', D: 'right' };

const walkable = (c: MineCellDto | null | undefined) => !!c && (c.t === 'open' || c.t === 'ladder' || c.t === 'chest');

/** Shortest walk through squares the player has seen (for the walking animation only). */
function pathBetween(dive: MineDiveDto, from: number, to: number): number[] {
  const { w, h, cells } = dive;
  const prev = new Map<number, number>([[from, -1]]);
  const queue = [from];
  while (queue.length) {
    const i = queue.shift()!;
    if (i === to) {
      const out: number[] = [];
      for (let p = to; p !== -1; p = prev.get(p)!) out.unshift(p);
      return out;
    }
    const x = i % w;
    for (const n of [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, i - w, i + w]) {
      if (n < 0 || n >= w * h || prev.has(n)) continue;
      if (n !== to && !walkable(cells[n])) continue;
      prev.set(n, i);
      queue.push(n);
    }
  }
  return [from, to];
}

/** Small seeded noise so each rock square keeps the same speckles from frame to frame. */
const hash = (n: number) => {
  let x = (n * 2654435761) >>> 0;
  x ^= x >>> 13;
  x = Math.imul(x, 1274126177) >>> 0;
  return (x >>> 0) / 4294967296;
};

function shade(hex: string, amount: number) {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v: number) => Math.max(0, Math.min(255, Math.round(v + amount * 255)));
  return `rgb(${ch((n >> 16) & 255)},${ch((n >> 8) & 255)},${ch(n & 255)})`;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  color: string;
  size: number;
}
interface FloatText {
  x: number;
  y: number;
  text: string;
  born: number;
  color: string;
}

export interface MineSceneProps {
  status: MineStatusDto;
  dive: MineDiveDto;
  look: AvatarLook;
  log: MineEventDto[];
  busy: boolean;
  /** Last error from the server (cleared by the parent). */
  error?: string;
  onAct: (i: number) => Promise<boolean>;
  onDescend: () => void;
  onClimb: () => void;
}

export function MineScene({ status, dive, look, log, busy, error, onAct, onDescend, onClimb }: MineSceneProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const zone = zoneFor(dive.floor);
  const pick = PICKAXES[status.gear.pickaxe];
  const lightRadius = LANTERNS[status.gear.lantern]!.light;
  const [hint, setHint] = useState<string>();
  const onLadder = dive.cells[dive.pos]?.t === 'ladder';

  // Everything the animation loop reads lives in one ref, so input never waits on a re-render.
  const live = useRef({
    dive,
    busy,
    shown: dive.pos,
    path: [] as number[],
    pathStart: 0,
    facing: 1 as 1 | -1,
    held: null as Dir | null,
    pointerTarget: null as number | null,
    digTarget: -1,
    digStart: 0,
    digNeed: 0,
    lastSwing: 0,
    particles: [] as Particle[],
    floats: [] as FloatText[],
    shake: 0,
    camX: 0,
    camY: 0,
    hinted: '',
  });
  live.current.dive = dive;
  live.current.busy = busy;

  // The miner walks from where it was drawn to wherever the server put it.
  const lastPos = useRef(dive.pos);
  const lastFloor = useRef(dive.floor);
  useEffect(() => {
    const L = live.current;
    if (lastFloor.current !== dive.floor) {
      lastFloor.current = dive.floor;
      lastPos.current = dive.pos;
      L.shown = dive.pos;
      L.path = [];
      L.camX = L.camY = Number.NaN;
      return;
    }
    if (lastPos.current !== dive.pos) {
      L.path = pathBetween(dive, L.shown, dive.pos);
      L.pathStart = performance.now();
      lastPos.current = dive.pos;
    }
  }, [dive]);

  // Events from the server become sparkles, floating words and a little camera shake.
  const seenLog = useRef<MineEventDto[]>(log);
  useEffect(() => {
    if (log === seenLog.current) return;
    const fresh = log.filter((e) => !seenLog.current.includes(e));
    seenLog.current = log;
    const L = live.current;
    const { x, y } = centre(dive.pos, dive.w);
    for (const e of fresh) {
      const color = e.kind === 'ore' ? '#ffe680' : e.kind === 'gas' ? '#b7e07a' : e.kind === 'rockfall' || e.kind === 'broken' ? '#ff8a7a' : e.kind === 'level' || e.kind === 'badge' ? '#9ef0ff' : '#ffffff';
      if (['ore', 'artifact', 'key', 'chest', 'level', 'badge', 'broken', 'worn', 'gas'].includes(e.kind)) {
        L.floats.push({ x, y: y - T * 0.6, text: e.kind === 'ore' ? e.text : e.text.length > 34 ? `${e.text.slice(0, 32)}…` : e.text, born: performance.now(), color });
      }
      if (e.kind === 'rockfall') {
        L.shake = 14;
        for (let k = 0; k < 18; k++) L.particles.push(debris(x + (Math.random() - 0.5) * T * 2, y - T * 1.5, '#8b8f96'));
      }
      if (e.kind === 'gas') for (let k = 0; k < 24; k++) L.particles.push({ x, y, vx: (Math.random() - 0.5) * 60, vy: -20 - Math.random() * 40, life: 0, max: 1.6, color: 'rgba(170,220,90,0.5)', size: 10 + Math.random() * 10 });
      if (e.kind === 'broken') play('snap');
    }
  }, [log, dive.pos, dive.w]);

  /** Starts walking, or decides what holding toward a square means. */
  const targetFor = useCallback((d: Dir) => {
    const { dive: dv } = live.current;
    const x = dv.pos % dv.w;
    const y = Math.floor(dv.pos / dv.w);
    const [dx, dy] = DIRS[d];
    if (x + dx < 0 || x + dx >= dv.w || y + dy < 0 || y + dy >= dv.h) return -1;
    return (y + dy) * dv.w + x + dx;
  }, []);

  const say = useCallback((text: string) => {
    if (live.current.hinted === text) return;
    live.current.hinted = text;
    setHint(text);
  }, []);

  // Input: keys, pointer and the on-screen pad all set what's being held; the loop does the rest.
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement | null)?.closest('input, textarea')) return;
      const dir = KEYS[e.key];
      if (dir) {
        e.preventDefault();
        live.current.held = dir;
        live.current.pointerTarget = null;
        if (dir === 'left' || dir === 'right') live.current.facing = dir === 'left' ? -1 : 1;
      } else if ((e.key === 'e' || e.key === 'E' || e.key === ' ') && live.current.dive.cells[live.current.dive.pos]?.t === 'ladder' && !live.current.busy) {
        e.preventDefault();
        onDescend();
      }
    };
    const up = (e: KeyboardEvent) => {
      const dir = KEYS[e.key];
      if (dir && live.current.held === dir) live.current.held = null;
    };
    const blur = () => {
      live.current.held = null;
      live.current.pointerTarget = null;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, [onDescend]);

  // The animation and input loop.
  const avatar = useMemo(() => {
    const scale = 0.92;
    const c = document.createElement('canvas');
    c.width = AVATAR_W * scale * 2;
    c.height = AVATAR_H * scale * 2;
    const g = c.getContext('2d')!;
    g.setTransform(scale * 2, 0, 0, scale * 2, 0, 0);
    drawAvatar(g, look);
    return { canvas: c, w: AVATAR_W * scale, h: AVATAR_H * scale };
  }, [look]);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const g = canvas.getContext('2d')!;
    let raf = 0;
    let last = performance.now();

    const tick = (now: number) => {
      const L = live.current;
      const dv = L.dive;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      // --- Walking animation -------------------------------------------------
      let mx: number;
      let my: number;
      let walking = false;
      if (L.path.length > 1) {
        const t = (now - L.pathStart) / STEP_MS;
        const k = Math.min(L.path.length - 1, Math.floor(t));
        const a = centre(L.path[k]!, dv.w);
        const b = centre(L.path[Math.min(L.path.length - 1, k + 1)]!, dv.w);
        const f = Math.min(1, t - k);
        mx = a.x + (b.x - a.x) * f;
        my = a.y + (b.y - a.y) * f;
        if (b.x !== a.x) L.facing = b.x < a.x ? -1 : 1;
        walking = t < L.path.length - 1;
        if (!walking) {
          L.shown = L.path[L.path.length - 1]!;
          L.path = [];
        }
      } else {
        L.shown = dv.pos;
        ({ x: mx, y: my } = centre(dv.pos, dv.w));
      }

      // --- What the player is holding toward ----------------------------------
      const target = L.pointerTarget ?? (L.held ? targetFor(L.held) : -1);
      const ready = !L.busy && !walking;
      let progress = 0;
      if (target < 0 || !ready) {
        if (target < 0 || L.busy) L.digTarget = -1;
      } else {
        const c = dv.cells[target];
        const adjacent = isAdjacent(dv.pos, target, dv.w);
        if (c && c.t === 'rock' && adjacent) {
          const hard = ROCKS[c.rock].hardness > pick.power;
          const cost = digCost(ROCKS[c.rock].hardness, status.gear.pickaxe);
          if (hard) say(`${ROCKS[c.rock].name} is too hard for your ${pick.name}.`);
          else if (dv.durability <= 0) say(`Your ${pick.name} is broken. Climb out and fix it at the Forge.`);
          else if (dv.energy < cost) say('Your lantern is too low to dig that. Climb out to keep your haul!');
          else {
            if (L.digTarget !== target) {
              L.digTarget = target;
              L.digStart = now;
              L.digNeed = digTimeMs({ rock: c.rock, ore: c.ore, artifact: c.artifact, key: c.key }, status.gear.pickaxe);
              L.lastSwing = 0;
              L.hinted = '';
            }
            const tx = target % dv.w;
            if (tx !== dv.pos % dv.w) L.facing = tx < dv.pos % dv.w ? -1 : 1;
            progress = Math.min(1, (now - L.digStart) / L.digNeed);
            // A swing every 320 ms: a tick of sound and a few chips flying.
            if (now - L.lastSwing > 320) {
              L.lastSwing = now;
              play('pick');
              const { x, y } = centre(target, dv.w);
              for (let k = 0; k < 4; k++) L.particles.push(debris(x - (x - mx) * 0.4, y - (y - my) * 0.4, ROCKS[c.rock].color));
            }
            if (progress >= 1) {
              const { x, y } = centre(target, dv.w);
              L.digTarget = -1;
              L.busy = true;
              void onAct(target).then((ok) => {
                if (ok) {
                  play('rockBreak');
                  L.shake = 5;
                  for (let k = 0; k < 16; k++) L.particles.push(debris(x, y, ROCKS[c.rock].color));
                  if (c.ore) for (let k = 0; k < 8; k++) L.particles.push(debris(x, y, ORES[c.ore].color));
                }
              });
              if (L.pointerTarget === target) L.pointerTarget = null;
            }
          }
        } else if (c && walkable(c)) {
          L.digTarget = -1;
          L.busy = true;
          void onAct(target);
          if (L.pointerTarget === target) L.pointerTarget = null;
        } else if (c && c.t === 'rock' && !adjacent) {
          say('Walk next to a rock to dig it.');
          L.pointerTarget = null;
        } else if (c?.t === 'lava') {
          say("That's lava! Find another way around.");
          L.pointerTarget = null;
        } else L.pointerTarget = null;
      }

      // --- Camera --------------------------------------------------------------
      const cw = canvas.clientWidth;
      const ch = canvas.clientHeight;
      const dpr = window.devicePixelRatio || 1;
      if (canvas.width !== Math.round(cw * dpr) || canvas.height !== Math.round(ch * dpr)) {
        canvas.width = Math.round(cw * dpr);
        canvas.height = Math.round(ch * dpr);
      }
      // About eight squares tall on any screen; never smaller than needed to show the whole width on phones.
      const zoom = Math.max(0.45, Math.min(1.4, ch / (T * 8.5), (cw / (T * Math.min(dv.w + 1, 11)))));
      const viewW = cw / zoom;
      const viewH = ch / zoom;
      const worldW = dv.w * T;
      const worldH = (dv.h + SURFACE_ROWS) * T;
      const clamp = (v: number, lo: number, hi: number) => (hi < lo ? (lo + hi) / 2 : Math.max(lo, Math.min(hi, v)));
      const wantX = clamp(mx - viewW / 2, -T, worldW + T - viewW);
      const wantY = clamp(my - viewH * 0.45, -SURFACE_ROWS * T, worldH - SURFACE_ROWS * T + T - viewH);
      if (Number.isNaN(L.camX)) {
        L.camX = wantX;
        L.camY = wantY;
      }
      L.camX += (wantX - L.camX) * Math.min(1, dt * 6);
      L.camY += (wantY - L.camY) * Math.min(1, dt * 6);
      L.shake = Math.max(0, L.shake - dt * 30);
      const sx = (Math.random() - 0.5) * L.shake;
      const sy = (Math.random() - 0.5) * L.shake;

      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.fillStyle = '#0b0807';
      g.fillRect(0, 0, cw, ch);
      g.setTransform(dpr * zoom, 0, 0, dpr * zoom, dpr * (-L.camX * zoom + sx), dpr * (-L.camY * zoom + sy));

      // --- Surface: sky, sand and the shaft mouth (floor 1 only; deeper floors show rock) ---
      const top = -SURFACE_ROWS * T;
      if (dv.floor === 1) {
        const sky = g.createLinearGradient(0, top, 0, 0);
        sky.addColorStop(0, '#7cc4e6');
        sky.addColorStop(1, '#f2d9a2');
        g.fillStyle = sky;
        g.fillRect(-T * 4, top - T * 6, worldW + T * 8, T * 6 + SURFACE_ROWS * T);
        g.fillStyle = '#e3c27d';
        g.fillRect(-T * 4, -T * 0.35, worldW + T * 8, T * 0.35);
        const startX = (dv.cells.findIndex((c, i) => i < dv.w && c?.t === 'open') % dv.w) * T;
        g.strokeStyle = '#6e4a26';
        g.lineWidth = 6;
        g.beginPath();
        g.moveTo(startX + 6, top + T * 0.6);
        g.lineTo(startX + 6, 0);
        g.moveTo(startX + T - 6, top + T * 0.6);
        g.lineTo(startX + T - 6, 0);
        g.moveTo(startX, top + T * 0.6);
        g.lineTo(startX + T, top + T * 0.6);
        g.stroke();
        g.fillStyle = '#ffffff';
        g.font = 'bold 14px system-ui, sans-serif';
        g.textAlign = 'center';
        g.fillText('Deep Mine', startX + T / 2, top + T * 0.45);
      } else {
        g.fillStyle = shade(zone.color, -0.35);
        g.fillRect(-T * 4, top - T * 6, worldW + T * 8, T * 6 + SURFACE_ROWS * T);
        g.fillStyle = 'rgba(255,255,255,0.7)';
        g.font = 'bold 16px system-ui, sans-serif';
        g.textAlign = 'center';
        g.fillText(`Floor ${dv.floor} · ${dv.zone}`, worldW / 2, top + T);
      }
      // Bedrock walls either side.
      g.fillStyle = '#17110e';
      g.fillRect(-T * 4, 0, T * 4, worldH);
      g.fillRect(worldW, 0, T * 4, worldH);

      // --- Squares -------------------------------------------------------------
      const x0 = Math.max(0, Math.floor(L.camX / T) - 1);
      const x1 = Math.min(dv.w - 1, Math.ceil((L.camX + viewW) / T) + 1);
      const y0 = Math.max(0, Math.floor(L.camY / T) - 1);
      const y1 = Math.min(dv.h - 1, Math.ceil((L.camY + viewH) / T) + 1);
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const i = y * dv.w + x;
          drawCell(g, dv.cells[i], i, x * T, y * T, now, zone.color);
        }
      }
      // Cracks on the square being dug.
      if (L.digTarget >= 0 && progress > 0) {
        const tx = (L.digTarget % dv.w) * T;
        const ty = Math.floor(L.digTarget / dv.w) * T;
        drawCracks(g, tx, ty, progress, L.digTarget);
      }

      // --- The miner -------------------------------------------------------------
      const digging = L.digTarget >= 0 && progress > 0;
      const bob = walking ? Math.abs(Math.sin(now / 70)) * 4 : 0;
      g.save();
      g.translate(mx, my + T / 2 - 4 - bob);
      g.scale(L.facing, 1);
      g.drawImage(avatar.canvas, -avatar.w / 2, -avatar.h, avatar.w, avatar.h);
      // Pickaxe: swings toward the rock while digging, rests on the shoulder otherwise.
      const swing = digging ? Math.sin(((now - L.digStart) / 320) * Math.PI * 2) : 0;
      const digDown = digging && Math.floor(L.digTarget / dv.w) > Math.floor(dv.pos / dv.w);
      const digUp = digging && Math.floor(L.digTarget / dv.w) < Math.floor(dv.pos / dv.w);
      const base = digDown ? 1.6 : digUp ? -0.6 : 0.4;
      g.translate(avatar.w * 0.25, -avatar.h * 0.42);
      g.rotate(digging ? base + swing * 0.7 : -0.9);
      g.strokeStyle = '#7a5530';
      g.lineWidth = 5;
      g.lineCap = 'round';
      g.beginPath();
      g.moveTo(-4, 0);
      g.lineTo(34, 0);
      g.stroke();
      g.strokeStyle = dv.durability <= 0 ? '#555' : pick.color;
      g.lineWidth = 7;
      g.beginPath();
      g.moveTo(30, -16);
      g.quadraticCurveTo(40, 0, 30, 16);
      g.stroke();
      if (dv.durability <= 0) {
        // A broken pickaxe has a crack through its head.
        g.strokeStyle = '#111';
        g.lineWidth = 2;
        g.beginPath();
        g.moveTo(33, -6);
        g.lineTo(36, 2);
        g.stroke();
      }
      g.restore();

      // --- Particles and floating words -----------------------------------------
      L.particles = L.particles.filter((p) => (p.life += dt) < p.max);
      for (const p of L.particles) {
        p.vy += 520 * dt * (p.size > 8 ? -0.05 : 1);
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        g.globalAlpha = Math.max(0, 1 - p.life / p.max);
        g.fillStyle = p.color;
        g.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
      }
      g.globalAlpha = 1;

      // --- Lantern light: everything outside the glow fades to dark --------------
      const energyShare = Math.max(0.15, dv.energy / dv.maxEnergy);
      const radius = (1.6 + lightRadius * 1.15) * T * (0.65 + 0.35 * energyShare) * (1 + Math.sin(now / 160) * 0.015);
      const glow = g.createRadialGradient(mx, my - T * 0.2, radius * 0.25, mx, my - T * 0.2, radius);
      glow.addColorStop(0, 'rgba(0,0,0,0)');
      glow.addColorStop(0.7, 'rgba(5,3,2,0.5)');
      glow.addColorStop(1, 'rgba(5,3,2,0.8)');
      g.fillStyle = glow;
      if (dv.floor !== 1 || my > T) g.fillRect(L.camX - T, L.camY - T, viewW + T * 2, viewH + T * 2);
      else g.fillRect(L.camX - T, 0, viewW + T * 2, viewH + T * 2);
      // Warm lantern tint.
      const warm = g.createRadialGradient(mx, my - T * 0.2, 0, mx, my - T * 0.2, radius * 0.6);
      warm.addColorStop(0, 'rgba(255,200,110,0.16)');
      warm.addColorStop(1, 'rgba(255,200,110,0)');
      g.fillStyle = warm;
      g.fillRect(mx - radius, my - radius, radius * 2, radius * 2);

      // Hold progress ring above the miner.
      if (digging) {
        g.lineWidth = 5;
        g.strokeStyle = 'rgba(0,0,0,0.45)';
        g.beginPath();
        g.arc(mx, my - avatar.h * 0.95, 13, 0, Math.PI * 2);
        g.stroke();
        g.strokeStyle = progress >= 1 ? '#7be08a' : '#ffd36b';
        g.beginPath();
        g.arc(mx, my - avatar.h * 0.95, 13, -Math.PI / 2, -Math.PI / 2 + progress * Math.PI * 2);
        g.stroke();
      }

      L.floats = L.floats.filter((f) => now - f.born < 1800);
      g.textAlign = 'center';
      g.font = 'bold 16px system-ui, sans-serif';
      for (const f of L.floats) {
        const a = (now - f.born) / 1800;
        g.globalAlpha = 1 - a;
        g.lineWidth = 4;
        g.strokeStyle = 'rgba(0,0,0,0.7)';
        g.strokeText(f.text, f.x, f.y - a * 40);
        g.fillStyle = f.color;
        g.fillText(f.text, f.x, f.y - a * 40);
      }
      g.globalAlpha = 1;

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [avatar, lightRadius, onAct, pick, say, status.gear.pickaxe, targetFor, zone.color]);

  // Press and hold a rock (or tap a tunnel to walk there).
  const cellAt = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const L = live.current;
    const dv = L.dive;
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    const zoom = Math.max(0.45, Math.min(1.4, ch / (T * 8.5), (cw / (T * Math.min(dv.w + 1, 11)))));
    const wx = (e.clientX - rect.left) / zoom + L.camX;
    const wy = (e.clientY - rect.top) / zoom + L.camY;
    const x = Math.floor(wx / T);
    const y = Math.floor(wy / T);
    if (x < 0 || x >= dv.w || y < 0 || y >= dv.h) return -1;
    return y * dv.w + x;
  };

  const energyPct = Math.round((dive.energy / dive.maxEnergy) * 100);
  const durPct = Math.round((dive.durability / Math.max(1, dive.maxDurability)) * 100);
  const padProps = (d: Dir) => ({
    onPointerDown: (e: React.PointerEvent) => {
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      live.current.held = d;
      live.current.pointerTarget = null;
      if (d === 'left' || d === 'right') live.current.facing = d === 'left' ? -1 : 1;
    },
    onPointerUp: () => {
      if (live.current.held === d) live.current.held = null;
    },
    onPointerCancel: () => {
      live.current.held = null;
    },
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  });

  useEffect(() => {
    if (!error) return;
    setHint(error);
  }, [error]);
  useEffect(() => {
    if (!hint) return;
    const id = window.setTimeout(() => {
      setHint(undefined);
      live.current.hinted = '';
    }, 3500);
    return () => window.clearTimeout(id);
  }, [hint]);

  return (
    <div className="mine-scene" style={{ ['--zone' as string]: zone.color }}>
      <canvas
        ref={canvasRef}
        className="mine-canvas"
        role="img"
        aria-label={`Floor ${dive.floor} of the Deep Mine. Use the arrow keys to walk, and hold one toward a rock to dig it.`}
        onPointerDown={(e) => {
          const i = cellAt(e);
          if (i < 0) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          live.current.pointerTarget = i;
          live.current.held = null;
        }}
        onPointerUp={() => {
          // Letting go of a rock stops digging it; a tap on a tunnel still walks there.
          const L = live.current;
          const c = L.pointerTarget !== null ? L.dive.cells[L.pointerTarget] : null;
          if (c?.t === 'rock') L.pointerTarget = null;
        }}
        onPointerCancel={() => {
          live.current.pointerTarget = null;
        }}
        onContextMenu={(e) => e.preventDefault()}
      />

      <div className="mine-hud">
        <div className="mine-hud-floor"><b>Floor {dive.floor}</b><span>{dive.zone}</span></div>
        <div className="mine-hud-meter">
          <span>Lantern</span>
          <div className={energyPct <= 20 ? 'meter low' : 'meter'} aria-label={`Lantern ${dive.energy} of ${dive.maxEnergy}`}><i style={{ width: `${energyPct}%` }} /></div>
          <small>{dive.energy} / {dive.maxEnergy}</small>
        </div>
        <div className="mine-hud-meter">
          <span>{pick.name}</span>
          <div className={dive.durability / Math.max(1, dive.maxDurability) <= WORN_SHARE ? 'meter low' : 'meter pick'} aria-label={`Pickaxe durability ${dive.durability} of ${dive.maxDurability}`}><i style={{ width: `${durPct}%` }} /></div>
          <small>{dive.durability <= 0 ? 'Broken!' : `${dive.durability} / ${dive.maxDurability}`}</small>
        </div>
        <div className="mine-hud-floor"><b>🎒 {dive.carried} / {dive.slots}</b><span>{dive.hasKey ? '🔑 chest key' : ' '}</span></div>
        <div className="mine-hud-actions">
          {onLadder && <button className="btn" disabled={busy} onClick={onDescend}>Climb down ({DESCEND_COST})</button>}
          <button className="btn ghost" disabled={busy} onClick={onClimb}>Climb out</button>
        </div>
      </div>

      {(hint || energyPct <= 20) && (
        <p className="mine-hint" role="status">{hint ?? 'Your lantern is running low. Climb out soon to keep your haul!'}</p>
      )}

      <ul className="mine-log" aria-live="polite">
        {log.slice(0, 4).map((e, i) => <li key={`${i}-${e.text}`} className={e.kind}>{e.text}</li>)}
      </ul>

      <div className="mine-pad" aria-label="Move and dig">
        <button aria-label="Up" className="up" {...padProps('up')}>▲</button>
        <button aria-label="Left" className="left" {...padProps('left')}>◀</button>
        <button aria-label="Right" className="right" {...padProps('right')}>▶</button>
        <button aria-label="Down" className="down" {...padProps('down')}>▼</button>
      </div>
      <p className="mine-help">Arrow keys or WASD to walk. Hold toward a rock to dig: harder rock takes longer. {onLadder ? 'Press E to climb down.' : 'Find the ladder to go deeper.'}</p>
    </div>
  );
}

const centre = (i: number, w: number) => ({ x: (i % w) * T + T / 2, y: Math.floor(i / w) * T + T / 2 });
const isAdjacent = (a: number, b: number, w: number) => Math.abs((a % w) - (b % w)) + Math.abs(Math.floor(a / w) - Math.floor(b / w)) === 1;
const debris = (x: number, y: number, color: string): Particle => ({
  x,
  y,
  vx: (Math.random() - 0.5) * 260,
  vy: -60 - Math.random() * 200,
  life: 0,
  max: 0.5 + Math.random() * 0.5,
  color,
  size: 3 + Math.random() * 5,
});

function drawCell(g: CanvasRenderingContext2D, c: MineCellDto | null | undefined, i: number, x: number, y: number, now: number, zoneColor: string) {
  if (!c) {
    // Unexplored: solid dark rock.
    g.fillStyle = '#100c0a';
    g.fillRect(x, y, T, T);
    g.fillStyle = 'rgba(255,255,255,0.025)';
    g.fillRect(x + hash(i) * T * 0.8, y + hash(i + 7) * T * 0.8, 4, 4);
    return;
  }
  if (c.t !== 'rock') {
    // Tunnel: the dark back wall of the cave.
    g.fillStyle = shade(zoneColor, -0.42);
    g.fillRect(x, y, T, T);
    g.fillStyle = 'rgba(0,0,0,0.18)';
    for (let k = 0; k < 3; k++) g.fillRect(x + hash(i * 3 + k) * (T - 10), y + hash(i * 5 + k) * (T - 6), 10, 3);
    if (c.t === 'ladder') {
      g.strokeStyle = '#a77a45';
      g.lineWidth = 5;
      g.beginPath();
      g.moveTo(x + T * 0.28, y + 2);
      g.lineTo(x + T * 0.28, y + T);
      g.moveTo(x + T * 0.72, y + 2);
      g.lineTo(x + T * 0.72, y + T);
      for (let r = 1; r <= 4; r++) {
        g.moveTo(x + T * 0.28, y + (T / 5) * r);
        g.lineTo(x + T * 0.72, y + (T / 5) * r);
      }
      g.stroke();
      // A faint glow so the way down is easy to spot.
      g.fillStyle = `rgba(255,220,140,${0.08 + Math.sin(now / 300) * 0.04})`;
      g.fillRect(x, y, T, T);
    } else if (c.t === 'chest') {
      g.fillStyle = '#8a5a2b';
      g.fillRect(x + T * 0.18, y + T * 0.45, T * 0.64, T * 0.45);
      g.fillStyle = '#a8713a';
      g.fillRect(x + T * 0.18, y + T * 0.35, T * 0.64, T * 0.16);
      g.fillStyle = '#f2c230';
      g.fillRect(x + T * 0.46, y + T * 0.5, T * 0.08, T * 0.12);
    } else if (c.t === 'lava') {
      const glow = 0.75 + Math.sin(now / 250 + i) * 0.15;
      g.fillStyle = `rgba(255,${Math.round(110 * glow)},40,1)`;
      g.fillRect(x, y + T * 0.25, T, T * 0.75);
      g.fillStyle = 'rgba(255,220,120,0.6)';
      for (let k = 0; k < 3; k++) {
        const bx = x + ((hash(i + k) * T + now / 30) % T);
        g.beginPath();
        g.arc(bx, y + T * 0.35 + Math.sin(now / 200 + k) * 4, 3, 0, Math.PI * 2);
        g.fill();
      }
    }
    return;
  }
  // Rock: its colour, lit from above, with speckles that never move.
  const base = ROCKS[c.rock].color;
  g.fillStyle = base;
  g.fillRect(x, y, T, T);
  g.fillStyle = shade(base, 0.08);
  g.fillRect(x, y, T, 5);
  g.fillStyle = shade(base, -0.14);
  g.fillRect(x, y + T - 6, T, 6);
  g.fillStyle = shade(base, -0.08);
  for (let k = 0; k < 5; k++) {
    const s = 3 + hash(i * 11 + k) * 6;
    g.fillRect(x + 4 + hash(i * 7 + k) * (T - 12), y + 6 + hash(i * 13 + k) * (T - 16), s, s * 0.7);
  }
  if (c.ore) {
    g.fillStyle = ORES[c.ore].color;
    for (let k = 0; k < 4; k++) {
      const ox = x + 12 + hash(i * 17 + k) * (T - 24);
      const oy = y + 12 + hash(i * 19 + k) * (T - 24);
      g.beginPath();
      g.moveTo(ox, oy - 6);
      g.lineTo(ox + 6, oy);
      g.lineTo(ox, oy + 6);
      g.lineTo(ox - 6, oy);
      g.closePath();
      g.fill();
    }
    if (ORES[c.ore].sell >= 30) {
      // Valuable ores twinkle.
      const tw = (Math.sin(now / 220 + i) + 1) / 2;
      g.fillStyle = `rgba(255,255,255,${tw * 0.8})`;
      g.fillRect(x + T * 0.6, y + T * 0.3, 3, 3);
    }
  }
  if (c.artifact || c.key) {
    g.font = `${T * 0.42}px system-ui, sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.globalAlpha = 0.75 + Math.sin(now / 250 + i) * 0.25;
    g.fillText(c.key ? '🔑' : '✨', x + T / 2, y + T / 2);
    g.globalAlpha = 1;
    g.textBaseline = 'alphabetic';
  }
}

function drawCracks(g: CanvasRenderingContext2D, x: number, y: number, progress: number, seed: number) {
  g.strokeStyle = `rgba(20,12,8,${0.4 + progress * 0.5})`;
  g.lineWidth = 2 + progress * 2;
  const cx = x + T / 2;
  const cy = y + T / 2;
  const arms = 3 + Math.floor(progress * 5);
  g.beginPath();
  for (let k = 0; k < arms; k++) {
    const a = hash(seed * 31 + k) * Math.PI * 2;
    const len = T * 0.5 * Math.min(1, progress * 1.4) * (0.6 + hash(seed + k) * 0.4);
    g.moveTo(cx, cy);
    const mx = cx + Math.cos(a + 0.3) * len * 0.5;
    const my = cy + Math.sin(a + 0.3) * len * 0.5;
    g.lineTo(mx, my);
    g.lineTo(cx + Math.cos(a) * len, cy + Math.sin(a) * len);
  }
  g.stroke();
  g.fillStyle = `rgba(0,0,0,${progress * 0.25})`;
  g.fillRect(x, y, T, T);
}
